import moment from "moment";

import { getInitialsFromFullName } from "../../../constants/constants";
import {
  AiAutomationProposal,
  AiClientProposalFields,
  AiProposalRef,
  AiRepeatProposal,
  AiSequenceProposal,
  AiWorkProposalFields,
} from "../../../slices/AiSlice";
import { SelectOptionType } from "../../../types/types";
import { formatPhoneNumber } from "../../contacts/modals/addContact/addContactForm.helpers";
import { AutomationDraft } from "../../work/pages/automation/automation.constants";
import {
  CreateMode,
  Frequency,
  getDefaultForm as getDefaultRepeatForm,
  MONTHLY_EVERY_OPTIONS,
  RecurringScheduleForm,
} from "../../work/pages/repeats/recurringSchedule";
import {
  SequenceForm,
  SequenceStopScope,
} from "../../work/pages/sequences/emailSequence.types";
import { AddWorkFormData } from "../../works/modals/addWork/model/addWork.types";
import {
  ActionProposal,
  actionRows,
  actionWarnings,
  isActionKind,
  normalizeActionProposal,
} from "./actionProposal.utils";
import { isInAppLink } from "./links";

/*
 * "Ask ProCharted" actions: the AI prepares a new work item or a new client
 * (`proposal` in the ask answer). Nothing is created here: the proposal only
 * pre-fills the app's normal New work / New organization / New individual
 * window, and the person clicks Create there.
 */

export type ProposalRef = {
  id: string;
  name: string;
  detail?: string;
  link: string | null;
};

export type WorkProposal = {
  kind: "create_work";
  summary: string;
  warnings: string[];
  fields: {
    customerId: string;
    customerName: string;
    customerType: "organization" | "individual";
    serviceId: string;
    workTypeName: string;
    name: string;
    startDate: string | null;
    dueDate: string | null;
    assignees: ProposalRef[];
    roleId: string;
    roleName: string;
    templateId: string;
    templateName: string;
    timeBudget: number;
    notes: string;
    initialStatus: "Ready" | "Scheduled" | "";
    /** The New work window's optional sections (null = not part of it). */
    repeat: ProposalRepeat | null;
    automation: ProposalAutomation | null;
    sequence: ProposalSequence | null;
  };
};

export type ProposalRepeat = {
  frequency: Exclude<Frequency, "never">;
  every: number;
  createMode: CreateMode;
  /** YYYY-MM-DD */
  repeatUntil: string;
};

type WorkConditionKey = "WorkStatus" | "ReachedDueDate" | "WorkReachesDate";
type WorkActionKey = "Assignee" | "Status" | "SendEmail" | "SendNotification";

export type ProposalAutomation = {
  conditionId: string;
  conditionKey: WorkConditionKey;
  conditionValue: string;
  conditionValueName: string;
  conditionExtraValue: string;
  actionId: string;
  actionKey: WorkActionKey;
  actionValue: string;
  actionValueName: string;
  actionExtraValue: string;
};

export type ProposalSequenceStep = {
  emailTemplateId: string;
  subject: string;
  body: string;
  sendMode: "immediately" | "date" | "afterDays";
  delayDays: number;
  scheduledDate: string;
};

export type ProposalSequence = {
  name: string;
  templateName: string;
  fromUserId: string;
  fromEmail: string;
  toEmail: string;
  stopScope: SequenceStopScope;
  replyStatusId: string;
  notifyUserIds: string[];
  steps: ProposalSequenceStep[];
};

/** Shown on the card whenever an email sequence is part of the proposal. */
export const SEQUENCE_WARNING =
  "Clicking Create will start emailing the client.";

export type ClientProposal = {
  kind: "create_client";
  summary: string;
  warnings: string[];
  possibleDuplicates: ProposalRef[];
  fields: {
    type: "organization" | "individual";
    name: string;
    companyName: string;
    firstName: string;
    middleName: string;
    lastName: string;
    contactPersonName: string;
    email: string;
    phone: string;
    address: string;
    contactType: ContactKindValue;
  };
};

/** New work / new client, or an action on existing work, billing or time (actionProposal.utils.ts). */
export type AskProposal = WorkProposal | ClientProposal | ActionProposal;

type ContactKindValue = "Client" | "Prospect" | "Other";
const CONTACT_KINDS: ContactKindValue[] = ["Client", "Prospect", "Other"];

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** "2027-04-30" (strict YYYY-MM-DD) or null. */
export const proposalDate = (value: unknown): string | null => {
  const raw = text(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) &&
    moment(raw, "YYYY-MM-DD", true).isValid()
    ? raw
    : null;
};

/** "Apr 30, 2027" for the card. */
export const formatProposalDate = (value: string | null): string =>
  value ? moment(value, "YYYY-MM-DD").format("MMM D, YYYY") : "";

/**
 * The New work window keeps dates as ISO strings with the current time of
 * day (its default is "now" and the date picker keeps that time), so a
 * proposed day gets the same: that local day at the current time.
 */
export const toWindowDate = (value: string | null, now = moment()): string => {
  const day = value ? moment(value, "YYYY-MM-DD", true) : null;
  if (!day || !day.isValid()) return now.toISOString();
  return moment(now)
    .year(day.year())
    .month(day.month())
    .date(day.date())
    .toISOString();
};

const normalizeRefs = (value: unknown): ProposalRef[] =>
  (Array.isArray(value) ? value : [])
    .filter(isRecord)
    .map((ref) => ({
      id: text((ref as AiProposalRef).id),
      name: text((ref as AiProposalRef).name),
      detail: text((ref as AiProposalRef).detail) || undefined,
      link: isInAppLink((ref as AiProposalRef).link)
        ? text((ref as AiProposalRef).link)
        : null,
    }))
    .filter((ref) => ref.name);

const normalizeWarnings = (value: unknown): string[] =>
  Array.from(
    new Set((Array.isArray(value) ? value : []).map(text).filter(Boolean)),
  );

const FREQUENCIES: ProposalRepeat["frequency"][] = [
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "annually",
];
const CREATE_MODES: CreateMode[] = ["completion", "specific", "immediately"];

/** The repeat schedule, only with values the Recurring work window offers. */
export const normalizeRepeat = (raw: unknown): ProposalRepeat | null => {
  if (!isRecord(raw)) return null;
  const r = raw as AiRepeatProposal;
  const asked = text(r.frequency).toLowerCase();
  const frequency = FREQUENCIES.find(
    (value) => value === (asked === "yearly" ? "annually" : asked),
  );
  const createMode = CREATE_MODES.find((mode) => mode === text(r.createMode));
  const repeatUntil = proposalDate(r.repeatUntil);
  if (!frequency || !createMode || !repeatUntil) return null;
  const every = Number(r.every);
  return {
    frequency,
    createMode,
    repeatUntil,
    every:
      frequency === "monthly" && MONTHLY_EVERY_OPTIONS.includes(every)
        ? every
        : 1,
  };
};

const CONDITION_KEYS: WorkConditionKey[] = [
  "WorkStatus",
  "ReachedDueDate",
  "WorkReachesDate",
];
const ACTION_KEYS: WorkActionKey[] = [
  "Assignee",
  "Status",
  "SendEmail",
  "SendNotification",
];

/** A work automation with every value the window needs, else null. */
export const normalizeAutomation = (
  raw: unknown,
): ProposalAutomation | null => {
  if (!isRecord(raw)) return null;
  const a = raw as AiAutomationProposal;
  const conditionKey = CONDITION_KEYS.find((k) => k === text(a.conditionKey));
  const actionKey = ACTION_KEYS.find((k) => k === text(a.actionKey));
  const automation = {
    conditionId: text(a.conditionId),
    conditionValue: text(a.conditionValue),
    conditionValueName: text(a.conditionValueName),
    conditionExtraValue: text(a.conditionExtraValue),
    actionId: text(a.actionId),
    actionValue: text(a.actionValue),
    actionValueName: text(a.actionValueName),
    actionExtraValue: text(a.actionExtraValue),
  };
  if (!conditionKey || !actionKey) return null;
  if (!automation.conditionId || !automation.actionId) return null;
  if (conditionKey === "WorkStatus" && !automation.conditionValue) return null;
  if (
    conditionKey === "WorkReachesDate" &&
    (!(Number(automation.conditionValue) > 0) ||
      !["before", "after"].includes(automation.conditionExtraValue))
  )
    return null;
  if (!automation.actionValue) return null;
  return { ...automation, conditionKey, actionKey };
};

const MAX_SEQUENCE_EMAILS = 10;

/** The email sequence draft (first email immediately or on a date). */
export const normalizeSequence = (raw: unknown): ProposalSequence | null => {
  if (!isRecord(raw)) return null;
  const q = raw as AiSequenceProposal;
  const steps = (Array.isArray(q.steps) ? q.steps : [])
    .filter(isRecord)
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
    .slice(0, MAX_SEQUENCE_EMAILS)
    .map((step, index): ProposalSequenceStep => {
      const date = proposalDate(step.scheduledDate);
      const days = Math.round(Number(step.delayDays));
      return {
        emailTemplateId: text(step.emailTemplateId),
        subject: text(step.subject),
        body: typeof step.body === "string" ? step.body : "",
        sendMode:
          index > 0
            ? "afterDays"
            : text(step.sendMode) === "date" && date
            ? "date"
            : "immediately",
        delayDays:
          index === 0 ? 0 : Number.isFinite(days) && days >= 1 ? days : 3,
        scheduledDate:
          index === 0 && text(step.sendMode) === "date" && date ? date : "",
      };
    });
  const fromUserId = text(q.fromUserId);
  if (
    !fromUserId ||
    !steps.length ||
    steps.some((step) => !step.subject || !step.body.trim())
  )
    return null;
  return {
    name: text(q.name) || text(q.templateName) || "Email sequence",
    templateName: text(q.templateName),
    fromUserId,
    fromEmail: text(q.fromEmail),
    toEmail: text(q.toEmail),
    stopScope: text(q.stopScope) === "mine" ? "mine" : "all",
    replyStatusId: text(q.replyStatusId),
    notifyUserIds: Array.isArray(q.notifyUserIds)
      ? q.notifyUserIds.map(text).filter(Boolean)
      : [],
    steps,
  };
};

const workFields = (raw: AiWorkProposalFields): WorkProposal["fields"] => {
  const ids = Array.isArray(raw.assignedUserIds)
    ? raw.assignedUserIds.map(text).filter(Boolean)
    : [];
  const people = normalizeRefs(raw.assignees).filter((ref) => ref.id);
  // Every assigned id gets a row, named when the AI gave a name.
  const assignees = Array.from(
    new Set([...ids, ...people.map((p) => p.id)]),
  ).map(
    (id) =>
      people.find((person) => person.id === id) || {
        id,
        name: "Team member",
        link: null,
      },
  );
  const budget = Number(raw.timeBudget);
  const status = text(raw.initialStatus);
  return {
    customerId: text(raw.customerId),
    customerName: text(raw.customerName),
    customerType:
      text(raw.customerType) === "organization" ? "organization" : "individual",
    serviceId: text(raw.serviceId),
    workTypeName: text(raw.workTypeName),
    name: text(raw.name),
    startDate: proposalDate(raw.startDate),
    dueDate: proposalDate(raw.dueDate),
    assignees,
    roleId: text(raw.roleId),
    roleName: text(raw.roleName),
    templateId: text(raw.templateId),
    templateName: text(raw.templateName),
    // The window takes whole hours only.
    timeBudget: Number.isFinite(budget) && budget > 0 ? Math.round(budget) : 0,
    notes: text(raw.notes),
    initialStatus: status === "Ready" || status === "Scheduled" ? status : "",
    repeat: normalizeRepeat(raw.repeat),
    automation: normalizeAutomation(raw.automation),
    sequence: normalizeSequence(raw.sequence),
  };
};

const joinName = (...parts: string[]) => parts.filter(Boolean).join(" ");

const clientFields = (
  raw: AiClientProposalFields,
): ClientProposal["fields"] => {
  const type = text(raw.type) === "individual" ? "individual" : "organization";
  const firstName = text(raw.firstName);
  const middleName = text(raw.middleName);
  const lastName = text(raw.lastName);
  const companyName = text(raw.companyName);
  const kind = CONTACT_KINDS.find(
    (option) => option.toLowerCase() === text(raw.contactType).toLowerCase(),
  );
  return {
    type,
    name:
      text(raw.name) ||
      (type === "organization"
        ? companyName
        : joinName(firstName, middleName, lastName)),
    companyName: companyName || (type === "organization" ? text(raw.name) : ""),
    firstName,
    middleName,
    lastName,
    contactPersonName:
      text(raw.contactPersonName) ||
      joinName(
        text(raw.contactPersonFirstName),
        text(raw.contactPersonLastName),
      ),
    email: text(raw.email),
    phone: text(raw.phone),
    address: text(raw.address),
    contactType: kind || "Client",
  };
};

const DUPLICATE_WARNING =
  /possible duplicate|already exists?|may already|similar name|same (name|email)/i;

/**
 * The card shows the possible duplicates once, as links ("This client may
 * already exist: …"), so warnings that only repeat that ("Possible
 * duplicate: organization "X" already exists (same name).") are left out
 * when the server listed the duplicates. Other warnings are kept.
 */
export const withoutDuplicateWarnings = (
  warnings: string[],
  duplicates: ProposalRef[],
): string[] =>
  duplicates.length
    ? warnings.filter((warning) => !DUPLICATE_WARNING.test(warning))
    : warnings;

/** Cleans the server's `proposal`; null when it isn't a known, usable one. */
export const normalizeProposal = (raw: unknown): AskProposal | null => {
  if (!isRecord(raw) || !isRecord(raw.fields)) return null;
  const summary = text(raw.summary);
  const warnings = normalizeWarnings(raw.warnings);
  if (raw.kind === "create_work") {
    const fields = workFields(raw.fields as AiWorkProposalFields);
    if (!fields.customerId && !fields.serviceId && !fields.name) return null;
    return {
      kind: "create_work",
      summary: summary || fields.name || "New work item",
      warnings,
      fields,
    };
  }
  if (raw.kind === "create_client") {
    const fields = clientFields(raw.fields as AiClientProposalFields);
    if (!fields.name) return null;
    const candidates = isRecord(raw.candidates) ? raw.candidates : {};
    const possibleDuplicates = normalizeRefs(candidates.possibleDuplicates);
    return {
      kind: "create_client",
      summary: summary || fields.name,
      warnings: withoutDuplicateWarnings(warnings, possibleDuplicates),
      fields,
      possibleDuplicates,
    };
  }
  if (isActionKind(raw.kind))
    return normalizeActionProposal(raw.kind, raw.fields, summary, warnings);
  return null;
};

export type ProposalRow = { label: string; value: string };

const FREQUENCY_TEXT: Record<ProposalRepeat["frequency"], string> = {
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Yearly",
};
const CREATE_MODE_TEXT: Record<CreateMode, string> = {
  completion: "on completion",
  specific: "on a set day",
  immediately: "all created at once",
};

/** "Yearly, on completion, until Oct 21, 2031" */
export const repeatText = (repeat: ProposalRepeat | null): string =>
  repeat
    ? `${
        repeat.frequency === "monthly" && repeat.every > 1
          ? `Every ${repeat.every} months`
          : FREQUENCY_TEXT[repeat.frequency]
      }, ${CREATE_MODE_TEXT[repeat.createMode]}, until ${formatProposalDate(
        repeat.repeatUntil,
      )}`
    : "";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "When the status is Waiting: send client email “Document request”" */
export const automationText = (
  automation: ProposalAutomation | null,
): string => {
  if (!automation) return "";
  const days = Number(automation.conditionValue);
  const when =
    automation.conditionKey === "WorkStatus"
      ? `When the status is ${automation.conditionValueName || "…"}`
      : automation.conditionKey === "ReachedDueDate"
      ? "When the due date is reached"
      : automation.conditionExtraValue === "before"
      ? `${plural(days, "day")} before the due date`
      : `${plural(days, "day")} after the start date`;
  const value = automation.actionValueName || "…";
  const what =
    automation.actionKey === "SendEmail"
      ? `send client email “${value}”${
          automation.actionExtraValue
            ? ` to ${automation.actionExtraValue}`
            : ""
        }`
      : automation.actionKey === "SendNotification"
      ? `notify ${value}`
      : automation.actionKey === "Assignee"
      ? `add ${value} to the work`
      : `change the status to ${value}`;
  return `${when}: ${what}`;
};

/** "Year-end documents, 3 emails to x@y.com (first one right away)" */
export const sequenceText = (sequence: ProposalSequence | null): string => {
  if (!sequence) return "";
  const first = sequence.steps[0];
  const when =
    first?.sendMode === "date"
      ? `first on ${formatProposalDate(first.scheduledDate)}`
      : "first one right away";
  const to = sequence.toEmail
    ? ` to ${sequence.toEmail}`
    : " to the client's primary email";
  const from = sequence.fromEmail ? `, from ${sequence.fromEmail}` : "";
  return `${sequence.templateName || sequence.name}, ${plural(
    sequence.steps.length,
    "email",
  )}${to} (${when}${from})`;
};

/** Key fields shown on the "Ready to create" card (empty ones left out). */
export const proposalRows = (proposal: AskProposal): ProposalRow[] => {
  if (proposal.kind !== "create_work" && proposal.kind !== "create_client")
    return actionRows(proposal).filter((row) => row.value);
  const rows: ProposalRow[] =
    proposal.kind === "create_work"
      ? [
          { label: "Client", value: proposal.fields.customerName },
          { label: "Work type", value: proposal.fields.workTypeName },
          { label: "Name", value: proposal.fields.name },
          {
            label: "Start date",
            value: formatProposalDate(proposal.fields.startDate),
          },
          {
            label: "Due date",
            value: formatProposalDate(proposal.fields.dueDate),
          },
          {
            label: "Assignees",
            value: proposal.fields.assignees.map((a) => a.name).join(", "),
          },
          { label: "Role", value: proposal.fields.roleName },
          { label: "Template", value: proposal.fields.templateName },
          {
            label: "Budget",
            value: proposal.fields.timeBudget
              ? `${proposal.fields.timeBudget} h`
              : "",
          },
          { label: "Note", value: proposal.fields.notes },
          { label: "Repeats", value: repeatText(proposal.fields.repeat) },
          {
            label: "Automation",
            value: automationText(proposal.fields.automation),
          },
          {
            label: "Email sequence",
            value: sequenceText(proposal.fields.sequence),
          },
        ]
      : [
          {
            label:
              proposal.fields.type === "organization"
                ? "Organization"
                : "Individual",
            value: proposal.fields.name,
          },
          { label: "Contact person", value: proposal.fields.contactPersonName },
          { label: "Email", value: proposal.fields.email },
          { label: "Phone", value: proposal.fields.phone },
          { label: "Address", value: proposal.fields.address },
          { label: "Contact type", value: proposal.fields.contactType },
        ];
  return rows.filter((row) => row.value);
};

/**
 * Warnings for the card: the server's messages plus the app's own checks
 * (dates the window will refuse, fields the window has no box for).
 */
export const proposalWarnings = (proposal: AskProposal): string[] => {
  const extra: string[] = [];
  if (proposal.kind === "create_work") {
    const { startDate, dueDate, customerId, serviceId } = proposal.fields;
    if (!customerId)
      extra.push("No client was matched: pick one in the window.");
    if (!serviceId)
      extra.push("No work type was matched: pick one in the window.");
    if (startDate && dueDate && startDate > dueDate) {
      extra.push("The due date is before the start date.");
    }
    if (proposal.fields.sequence) extra.push(SEQUENCE_WARNING);
  } else if (proposal.kind !== "create_client") {
    extra.push(...actionWarnings(proposal));
  } else if (
    proposal.fields.address &&
    // The server may already say so (in its own words).
    !proposal.warnings.some((warning) => /address/i.test(warning))
  ) {
    extra.push(
      "The address isn't part of the New client window: add it on the client's profile after creating.",
    );
  }
  return Array.from(new Set([...proposal.warnings, ...extra]));
};

const personOption = (person: ProposalRef): SelectOptionType => ({
  label: person.name,
  value: person.id,
  avatar: getInitialsFromFullName(person.name),
  type: "custom",
});

export type WorkWindowValues = {
  defaultContact?: SelectOptionType;
  values: Partial<AddWorkFormData>;
  note: string;
  /** Pre-filled "Add a repeat schedule" / automation / email sequence. */
  repeat: RecurringScheduleForm | null;
  automation: AutomationDraft | null;
  sequence: SequenceForm | null;
};

const localDay = (value: string | null, now: moment.Moment) =>
  (value ? moment(value, "YYYY-MM-DD", true) : moment(now))
    .startOf("day")
    .toDate();

/**
 * The window's repeat form: its defaults for the work's start and due dates
 * (create day, due day, months), with the proposed frequency, "Create work"
 * option and end date.
 */
export const repeatWindowForm = (
  repeat: ProposalRepeat,
  startDate: string | null,
  dueDate: string | null,
  now = moment(),
): RecurringScheduleForm => ({
  ...getDefaultRepeatForm(
    {
      startDate: localDay(startDate, now),
      dueDate: localDay(dueDate || startDate, now),
    },
    now.toDate(),
  ),
  frequency: repeat.frequency,
  every: repeat.every,
  createMode: repeat.createMode,
  repeatUntil: localDay(repeat.repeatUntil, now),
});

/** The window's AutomationDraft (scope "work"). */
export const automationWindowDraft = (
  automation: ProposalAutomation,
): AutomationDraft => ({
  scope: "work",
  conditionId: automation.conditionId,
  conditionKey: automation.conditionKey,
  conditionValue: automation.conditionValue,
  conditionExtraValue: automation.conditionExtraValue,
  actionId: automation.actionId,
  actionKey: automation.actionKey,
  actionValue: automation.actionValue,
  actionExtraValue: automation.actionExtraValue,
});

/** The window's SequenceForm ("New-work draft" mode). */
export const sequenceWindowForm = (
  sequence: ProposalSequence,
): SequenceForm => ({
  name: sequence.name,
  fromUserId: sequence.fromUserId,
  toEmail: sequence.toEmail,
  stopScope: sequence.stopScope,
  replyStatusId: sequence.replyStatusId,
  notifyUserIds: sequence.notifyUserIds,
  steps: sequence.steps.map((step, index) => ({
    key: `ai-step-${index + 1}`,
    emailTemplateId: step.emailTemplateId,
    subject: step.subject,
    body: step.body,
    sendMode: step.sendMode,
    delayDays: step.delayDays,
    scheduledDate: step.scheduledDate,
  })),
});

/** New work window pre-fill (same field shapes the window itself uses). */
export const workWindowValues = (
  proposal: WorkProposal,
  now = moment(),
): WorkWindowValues => {
  const f = proposal.fields;
  const startDate = toWindowDate(f.startDate, now);
  const dueDate = f.dueDate ? toWindowDate(f.dueDate, now) : startDate;
  const values: Partial<AddWorkFormData> = {
    workName: f.name,
    workType: f.serviceId
      ? { value: f.serviceId, label: f.workTypeName || "Work type" }
      : null,
    subtaskTemplate: f.templateId
      ? { value: f.templateId, label: f.templateName || "Template" }
      : null,
    role: f.roleId ? { value: f.roleId, label: f.roleName || "Role" } : null,
    budgetedTime: String(f.timeBudget || 0),
    startDate,
    dueDate,
  };
  // Without proposed people the window keeps its own default (me).
  if (f.assignees.length) values.assignedTo = f.assignees.map(personOption);
  else if (f.roleId) values.assignedTo = [];
  return {
    defaultContact: f.customerId
      ? {
          value: f.customerId,
          label: f.customerName || "Client",
          ...(f.customerType === "organization"
            ? { avatarColor: "purple" as const }
            : {}),
        }
      : undefined,
    values,
    note: f.notes,
    repeat: f.repeat
      ? repeatWindowForm(f.repeat, f.startDate, f.dueDate, now)
      : null,
    automation: f.automation ? automationWindowDraft(f.automation) : null,
    sequence: f.sequence ? sequenceWindowForm(f.sequence) : null,
  };
};

export type OrganizationWindowValues = {
  companyName: string;
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  contactType: ContactKindValue;
};

export const organizationWindowValues = (
  proposal: ClientProposal,
): OrganizationWindowValues => ({
  companyName: (proposal.fields.companyName || proposal.fields.name).slice(
    0,
    50,
  ),
  primaryContactName: proposal.fields.contactPersonName,
  primaryContactPhone: windowPhone(proposal.fields.phone),
  primaryContactEmail: proposal.fields.email,
  contactType: proposal.fields.contactType,
});

export type IndividualWindowValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone: string;
  contactType: ContactKindValue;
};

export const individualWindowValues = (
  proposal: ClientProposal,
): IndividualWindowValues => {
  const f = proposal.fields;
  let { firstName, lastName } = f;
  if (!firstName && !lastName) {
    const parts = f.name.split(/\s+/).filter(Boolean);
    firstName = parts[0] || "";
    lastName = parts.slice(1).join(" ");
  }
  return {
    firstName,
    middleName: f.middleName,
    lastName,
    displayName: joinName(firstName, f.middleName, lastName),
    email: f.email,
    phone: windowPhone(f.phone),
    contactType: f.contactType,
  };
};

/**
 * Phone as the client windows type it, "(416) 555-0199" (10 digits; a
 * leading country code 1 is dropped).
 */
export const windowPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return formatPhoneNumber(local);
};

/** Toast after the window created it. */
export const createdToast = (proposal: AskProposal): string =>
  proposal.kind === "create_work"
    ? "Work item created."
    : proposal.kind === "add_time"
    ? "Time added."
    : proposal.kind === "start_timer"
    ? "Timer started."
    : proposal.kind !== "create_client"
    ? "Saved."
    : proposal.fields.type === "organization"
    ? "Organization created."
    : "Individual created.";
